import { useTranslation } from 'react-i18next'
import { DataItem } from 'scrivito'

import { Button } from '@/Atomic/Molecules/Button'
import { unLinkDataItem } from '@/Atomic/Organisms/RelationalSublist'
import { SublistItem, SublistItemContent } from '@/Atomic/Templates/Sublist'
import { DataClassName } from '@/Data/const'
import { SchemaAttributes } from '@/Data/typesV2'
import { useUndoableAction } from '@/hooks/useUndoableAction'
import { getEnumDisplayValue } from '@/utils/CRUD/read/enum/getDisplayValue'
import { getSchemaAttributes } from '@/utils/CRUD/read/schema'
import { getSchemaName } from '@/utils/dataClassMap'
import { formatDateTime } from '@/utils/formatDateTime'

interface OrderSublistItemProps {
  order: DataItem
  relation?: DataItem
  flash?: boolean
  onOpen: (order: DataItem) => void
}

const formatAmount = (rawValue: number | undefined) => {
  if (typeof rawValue !== 'number' || !Number.isFinite(rawValue)) return ''
  return Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rawValue)
}

export const buildOrderSubtitle = (
  order: DataItem,
  schemaAttributes?: SchemaAttributes,
) => {
  const totalPriceLabel = schemaAttributes?.totalPrice?.title || 'Revenue'
  const dateLabel = schemaAttributes?.orderAt?.title || 'Order date'
  const statusLabel = schemaAttributes?.status?.title || 'Status'
  const typeAttribute =
    schemaAttributes?.type || schemaAttributes?.projectEntity
  const typeLabel = typeAttribute?.title || 'Type'

  const status = order.get('status') as string
  const type =
    (order.get('type') as string) || (order.get('projectEntity') as string)
  const dateValue = order.get('orderAt') as Date | undefined
  const totalPrice = order.get('totalPrice') as number | undefined
  const currency = (order.get('totalPriceCurrency') as string) || ''

  const statusDisplayValue =
    getEnumDisplayValue(schemaAttributes?.status?.enumValues, status) ?? status
  const typeDisplayValue =
    getEnumDisplayValue(typeAttribute?.enumValues, type) ?? type

  const amount = formatAmount(totalPrice)
  const amountWithCurrency = [amount, currency].filter(Boolean).join(' ')

  return [
    amountWithCurrency ? `${totalPriceLabel}: ${amountWithCurrency}` : '',
    dateValue ? `${dateLabel}: ${formatDateTime(dateValue, 'PP')}` : '',
    statusDisplayValue ? `${statusLabel}: ${statusDisplayValue}` : '',
    typeDisplayValue ? `${typeLabel}: ${typeDisplayValue}` : '',
  ]
    .filter(Boolean)
    .join(' · ')
}

export const OrderSublistItem: React.FC<OrderSublistItemProps> = ({
  order,
  relation,
  flash,
  onOpen,
}) => {
  const { t } = useTranslation()
  const { isHidden, run } = useUndoableAction()
  const { schemaAttributes } = getSchemaAttributes(
    getSchemaName(DataClassName.ORDER),
  )

  const displayTitle =
    (order.get('displayTitle') as string) ||
    (order.get('keyword') as string) ||
    ''

  if (relation && isHidden(relation.id())) return null

  return (
    <SublistItem flash={flash}>
      <SublistItemContent
        avatarIcon="file-check"
        title={displayTitle}
        subtitle={buildOrderSubtitle(order, schemaAttributes)}
        onClick={() => onOpen(order)}
        showButton="onHover"
        button={
          relation ? (
            <Button
              variant="icon"
              icon="cross"
              tooltip={t('common.remove')}
              onClick={() => {
                const relationId = relation.id()

                run({
                  id: relationId,
                  onAction: ({ onError, onUndo }) =>
                    unLinkDataItem({
                      relationDataItem: relation,
                      removedListItem: order,
                      onError,
                      onUndo,
                    }),
                })
              }}
            />
          ) : undefined
        }
      />
    </SublistItem>
  )
}
